import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../services/HttpManager";
import { Coberturas, Movimiento, ProspectCatalogs, ProspectFilters, Reparto, SalidaPreview } from "../../../services/v2/prospects.service";
import { cuando, errorTexto } from "./salesHelpers";

type Cand = { id: string; name: string; team?: string; n?: number };

const n = (v: number | undefined | null) => (v || 0).toLocaleString("es-MX");

/** "2026-10-15" → "15 oct" (fechas de calendario, sin hora). */
const dia = (d: string | null | undefined) => {
	if (!d) return "";
	const [y, m, dd] = d.slice(0, 10).split("-").map(Number);
	return new Date(y, m - 1, dd).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
};

const porEquipo = (cs: Cand[]) => {
	const g: { [t: string]: Cand[] } = {};
	for (const c of cs) (g[c.team || "Otras"] = g[c.team || "Otras"] || []).push(c);
	return Object.keys(g).sort().map((t) => ({ team: t, people: g[t] }));
};

/** Una asesora (lista) o varias (casillas por equipo, con "todo el equipo"). Muestra cuántas tiene hoy cada una si se sabe. */
export const ElegirAsesoras: React.FC<{ candidatas: Cand[]; value: string[]; onChange: (v: string[]) => void; multi: boolean; label?: string }> = ({ candidatas, value, onChange, multi, label }) => {
	const grupos = porEquipo(candidatas);
	const carga = (c: Cand) => (c.n !== undefined ? " · tiene " + n(c.n) : "");
	if (!multi) {
		return (
			<label>{label || "A quién"}
				<select value={value[0] || ""} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}>
					<option value="">Elige…</option>
					{grupos.map((g) => <optgroup key={g.team} label={g.team}>{g.people.map((c) => <option key={c.id} value={c.id}>{c.name}{carga(c)}</option>)}</optgroup>)}
				</select>
			</label>
		);
	}
	const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
	return (
		<div className="mv-elegir">
			{label ? <span className="mv-titulo">{label}{value.length ? ` · ${value.length} elegidas` : ""}</span> : null}
			{grupos.map((g) => {
				const ids = g.people.map((c) => c.id);
				const todas = ids.every((id) => value.includes(id));
				return (
					<div key={g.team} className="mv-equipo">
						<div className="mv-equipo-cab">
							<b>{g.team}</b>
							<button className="enlace" onClick={() => onChange(todas ? value.filter((x) => !ids.includes(x)) : Array.from(new Set([...value, ...ids])))}>
								{todas ? "Quitar el equipo" : "Todo el equipo"}
							</button>
						</div>
						<div className="mv-personas">
							{g.people.map((c) => (
								<label key={c.id} className="mv-op">
									<input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} />
									<span>{c.name}{c.n !== undefined ? <span className="apagado"> · {n(c.n)}</span> : null}</span>
								</label>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const RepartoLista: React.FC<{ reparto: Reparto[]; extra?: string }> = ({ reparto, extra }) => (
	<div className="mv-reparto">
		{reparto.map((r) => <div key={r.id} className="par"><span>{r.name}</span><span>recibe {n(r.n)}</span></div>)}
		{extra ? <div className="apagado">{extra}</div> : null}
	</div>
);

/**
 * Mover en bloque desde la Cartera: a una asesora o repartido parejo entre varias. Primero dice cuántas le tocan
 * a cada una (sin mover nada); luego mueve, y el movimiento se puede deshacer en "Salidas y coberturas".
 */
export const MoverModal: React.FC<{
	campusId: number; catalogs: ProspectCatalogs | null; ids: number[]; todos: boolean; filtros: ProspectFilters; cuantos: number;
	onClose: () => void; onHecho: (r: { batchId: number; reasignados: number }) => void;
}> = ({ campusId, catalogs, ids, todos, filtros, cuantos, onClose, onHecho }) => {
	const http = HttpManager.getInstance();
	const [modo, setModo] = useState<"una" | "varias">("una");
	const [una, setUna] = useState<string[]>([]);
	const [varias, setVarias] = useState<string[]>([]);
	const [motivo, setMotivo] = useState<string>("");
	const [vista, setVista] = useState<{ mover: number; yaSuyas: number; porAsesora: Reparto[] } | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const candidatas: Cand[] = (catalogs?.advisors || []).map((a) => ({ id: a.id, name: a.name, team: a.team }));
	const to = modo === "una" ? una : varias;
	useEffect(() => { setVista(null); }, [modo, to.join(",")]);
	const cuerpo = () => (todos ? { all: true, filters: filtros, to } : { ids, to });

	const ver = async () => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.reasignar(campusId, { ...cuerpo(), dry: true });
			setVista({ mover: r.data.mover || 0, yaSuyas: r.data.yaSuyas || 0, porAsesora: r.data.porAsesora });
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const mover = async () => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.reasignar(campusId, { ...cuerpo(), motivo: motivo.trim() || null });
			onHecho({ batchId: r.data.batchId || 0, reasignados: r.data.reasignados || 0 });
		} catch (e) { setError(errorTexto(e)); setBusy(false); }
	};

	return (
		<Modal show onHide={onClose} size="lg" className="ventas-nuevo-modal">
			<Modal.Header closeButton><Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>Mover {n(cuantos)} {cuantos === 1 ? "persona" : "personas"}</Modal.Title></Modal.Header>
			<Modal.Body className="mv">
				<div className="cv-modo" role="tablist">
					<button role="tab" aria-selected={modo === "una"} className={modo === "una" ? "on" : ""} onClick={() => setModo("una")}>A una asesora</button>
					<button role="tab" aria-selected={modo === "varias"} className={modo === "varias" ? "on" : ""} onClick={() => setModo("varias")}>Repartir parejo entre varias</button>
				</div>
				{modo === "una" ? <ElegirAsesoras candidatas={candidatas} value={una} onChange={setUna} multi={false} />
					: <ElegirAsesoras candidatas={candidatas} value={varias} onChange={setVarias} multi label="Entre quiénes" />}
				<label>Motivo (queda en la bitácora de cada persona)
					<input value={motivo} maxLength={200} placeholder="Opcional" onChange={(e) => setMotivo(e.target.value)} />
				</label>
				{vista ? <RepartoLista reparto={vista.porAsesora} extra={vista.yaSuyas ? `${n(vista.yaSuyas)} ya eran de quien las recibe y se quedan igual.` : undefined} /> : null}
				{error ? <div className="aviso error">{error}</div> : null}
			</Modal.Body>
			<Modal.Footer>
				<button className="btn-wa" onClick={onClose}>Cancelar</button>
				{!vista ? <button className="btn-wa pri" disabled={busy || !to.length} onClick={ver}>{busy ? "Calculando…" : "Ver cómo queda"}</button>
					: <button className="btn-wa pri" disabled={busy || !vista.mover} onClick={mover}>{busy ? "Moviendo…" : `Mover ${n(vista.mover)}`}</button>}
			</Modal.Footer>
		</Modal>
	);
};

const ETAPA_NOMBRE: { [k: string]: string } = { 1: "Pre-Inscripción", 2: "Matriculado", 3: "Retirado", 4: "Graduado" };

/** Pestaña "Se va": elegir a la asesora, ver todo lo que tiene, decidir cómo se reparte y confirmar. */
const SeVa: React.FC<{ campusId: number; onHecho: () => void }> = ({ campusId, onHecho }) => {
	const http = HttpManager.getInstance();
	const [base, setBase] = useState<SalidaPreview | null>(null);
	const [from, setFrom] = useState<string>("");
	const [info, setInfo] = useState<SalidaPreview | null>(null);
	const [modo, setModo] = useState<"una" | "parejo" | "por_tipo">("parejo");
	const [to, setTo] = useState<string[]>([]);
	const [top, setTop] = useState<string[]>([]);
	const [motivo, setMotivo] = useState<string>("");
	const [vista, setVista] = useState<{ porAsesora: Reparto[]; gestiones: number } | null>(null);
	const [hecho, setHecho] = useState<{ batchId: number; reasignados: number; gestiones: number; contactos: number } | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => { http.prospectsService.salidaPreview(campusId).then((r) => setBase(r.data)).catch((e) => setError(errorTexto(e))); }, [campusId]);
	useEffect(() => {
		setInfo(null); setVista(null); setHecho(null); setTo([]); setTop([]); setError(null);
		if (!from) return;
		http.prospectsService.salidaPreview(campusId, from).then((r) => setInfo(r.data)).catch((e) => setError(errorTexto(e)));
	}, [from]);
	useEffect(() => { setVista(null); }, [modo, to.join(","), top.join(",")]);

	const a = info?.asesora;
	const candidatas: Cand[] = info?.candidatas || base?.candidatas || [];
	const listo = modo === "una" ? to.length === 1 : modo === "parejo" ? to.length > 0 : top.length > 0;
	const cuerpo = () => ({ from, mode: modo, to, top: modo === "por_tipo" ? top : undefined, motivo: motivo.trim() || null });

	const ver = async () => {
		setBusy(true); setError(null);
		try { const r = await http.prospectsService.salida(campusId, { ...cuerpo(), dry: true }); setVista({ porAsesora: r.data.porAsesora, gestiones: r.data.gestiones }); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const confirmar = async () => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.salida(campusId, cuerpo());
			setHecho({ batchId: r.data.batchId || 0, reasignados: r.data.reasignados || 0, gestiones: r.data.gestiones, contactos: r.data.contactos || 0 });
			onHecho();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const deshacer = async () => {
		if (!hecho) return;
		setBusy(true); setError(null);
		try { await http.prospectsService.deshacer(campusId, hecho.batchId); setHecho(null); setFrom(""); onHecho(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	if (hecho && a) {
		return (
			<div className="mv-hecho">
				<div className="aviso ok">
					Listo: {n(hecho.reasignados)} personas de {a.name} repartidas{hecho.gestiones ? `, ${n(hecho.gestiones)} gestiones` : ""}{hecho.contactos ? `, ${n(hecho.contactos)} contactos de campaña` : ""}.
					Quedó fuera de la rotación de WAPP{a.extension ? ` y su extensión ${a.extension.extension} se apagó` : ""}. Cada persona tiene la nota en su bitácora.
				</div>
				<div><button className="btn-wa" disabled={busy} onClick={deshacer}>Deshacer esta salida</button></div>
			</div>
		);
	}

	return (
		<div className="mv-col">
			<label>Quién se va
				<select value={from} onChange={(e) => setFrom(e.target.value)}>
					<option value="">{base ? "Elige…" : "Cargando…"}</option>
					{(base?.owners || []).map((o) => <option key={o.id} value={o.id}>{o.name} · {n(o.n)}</option>)}
				</select>
			</label>
			{from && !a && !error ? <div className="ventas-vacio">Cargando lo que tiene…</div> : null}
			{a ? (
				<>
					<div className="tarjeta">
						<h4>{a.name} tiene {n(a.total)} en este plantel{a.activa ? "" : " · ya no está activa en Zoho"}</h4>
						<div className="mv-cifras">
							{Object.keys(a.etapas).filter((k) => a.etapas[k]).map((k) => <span key={k}>{ETAPA_NOMBRE[k]} <b>{n(a.etapas[k])}</b></span>)}
						</div>
						<div className="mv-cifras">
							<span>Interesadas y Promesas <b>{n(a.calientes)}</b></span>
							<span>Seguimientos agendados <b>{n(a.seguimientos)}</b></span>
							<span>Gestiones abiertas <b>{n(a.gestiones)}</b></span>
						</div>
						{a.campanas.length ? <div className="apagado">Campañas: {a.campanas.map((c) => `${c.name}${c.pendientes ? ` (${n(c.pendientes)} por llamar)` : ""}`).join(" · ")}</div> : null}
						<div className="apagado">
							{a.enRotacion ? "Está en la rotación de WAPP" : "No está en la rotación de WAPP"}{a.extension ? ` · extensión ${a.extension.extension}${a.extension.enabled ? "" : " (apagada)"}` : " · sin extensión"}
							{a.cobertura ? ` · la cubre ${a.cobertura.cover_name}` : ""}
						</div>
						{a.fueraDelPlantel ? <div className="aviso">Tiene {n(a.fueraDelPlantel)} más en otros planteles: repite la salida desde ese plantel.</div> : null}
					</div>

					<div className="cv-modo" role="tablist">
						<button role="tab" aria-selected={modo === "parejo"} className={modo === "parejo" ? "on" : ""} onClick={() => setModo("parejo")}>Parejo entre varias</button>
						<button role="tab" aria-selected={modo === "una"} className={modo === "una" ? "on" : ""} onClick={() => setModo("una")}>Todo a una</button>
						<button role="tab" aria-selected={modo === "por_tipo"} className={modo === "por_tipo" ? "on" : ""} onClick={() => setModo("por_tipo")}>Interesadas aparte</button>
					</div>
					{modo === "una" ? <ElegirAsesoras candidatas={candidatas} value={to} onChange={setTo} multi={false} label="Quién recibe todo" /> : null}
					{modo === "parejo" ? <ElegirAsesoras candidatas={candidatas} value={to} onChange={setTo} multi label="Entre quiénes (a cada una le toca la misma mezcla)" /> : null}
					{modo === "por_tipo" ? (
						<>
							<ElegirAsesoras candidatas={candidatas} value={top} onChange={setTop} multi label={`Interesadas y Promesas (${n(a.calientes)}) para`} />
							<ElegirAsesoras candidatas={candidatas} value={to} onChange={setTo} multi label="El resto para (si no eliges, va a las mismas)" />
						</>
					) : null}
					<label>Motivo (queda en la bitácora de cada persona)
						<input value={motivo} maxLength={200} placeholder={"Salida de " + a.name} onChange={(e) => setMotivo(e.target.value)} />
					</label>
					<div className="apagado">Al confirmar: sus gestiones abiertas y su gente pendiente en campañas pasan a quien recibió a cada persona; sale de las campañas y de la rotación de WAPP, y su extensión se apaga. Sus grabaciones y su historial se quedan.</div>
					{vista ? <RepartoLista reparto={vista.porAsesora} extra={vista.gestiones ? `Y ${n(vista.gestiones)} gestiones abiertas.` : undefined} /> : null}
					{error ? <div className="aviso error">{error}</div> : null}
					<div className="mv-botones">
						{!vista ? <button className="btn-wa pri" disabled={busy || !listo} onClick={ver}>{busy ? "Calculando…" : "Ver cómo queda"}</button>
							: <button className="btn-wa pri" disabled={busy} onClick={confirmar}>{busy ? "Moviendo…" : `Confirmar salida de ${a.name}`}</button>}
					</div>
				</>
			) : error ? <div className="aviso error">{error}</div> : null}
		</div>
	);
};

/** Pestaña "Cubre": mientras una asesora falta, otra atiende su cartera sin que cambie de dueña. */
const Cubre: React.FC<{ campusId: number; catalogs: ProspectCatalogs | null }> = ({ campusId, catalogs }) => {
	const http = HttpManager.getInstance();
	const [data, setData] = useState<Coberturas | null>(null);
	const [ausente, setAusente] = useState<string[]>([]);
	const [cubre, setCubre] = useState<string[]>([]);
	const [desde, setDesde] = useState<string>("");
	const [hasta, setHasta] = useState<string>("");
	const [motivo, setMotivo] = useState<string>("Vacaciones");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => { http.prospectsService.coberturas(campusId).then((r) => { setData(r.data); setDesde(r.data.hoy); }).catch((e) => setError(errorTexto(e))); }, [campusId]);
	const candidatas: Cand[] = (catalogs?.advisors || []).map((a) => ({ id: a.id, name: a.name, team: a.team }));
	const guardar = async () => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.guardarCobertura(campusId, { absent: ausente[0], cover: cubre[0], desde: desde || null, hasta: hasta || null, motivo });
			setData(r.data); setAusente([]); setCubre([]); setHasta("");
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const terminar = async (id: number) => {
		setError(null);
		try { setData((await http.prospectsService.terminarCobertura(campusId, id)).data); } catch (e) { setError(errorTexto(e)); }
	};
	return (
		<div className="mv-col">
			<div className="apagado">La gente de quien falta aparece en "Mi cartera" y en las campañas de quien la cubre. No cambia de dueña: al terminar, todo sigue siendo suyo. Mientras falta, la rotación de WAPP no le da nada nuevo.</div>
			{data?.vigentes.length ? (
				<div className="mv-lista">
					{data.vigentes.map((c) => (
						<div key={c.id} className="mv-fila">
							<span><b>{c.cover_name}</b> cubre a <b>{c.absent_name}</b><span className="apagado"> · {c.activa ? "desde" : "empieza el"} {dia(c.starts_on)}{c.ends_on ? " hasta el " + dia(c.ends_on) : ", sin fecha de regreso"}{c.reason ? " · " + c.reason : ""}</span></span>
							<button className="btn-wa" onClick={() => terminar(c.id)}>{c.activa ? "Ya regresó" : "Cancelar"}</button>
						</div>
					))}
				</div>
			) : data ? <div className="apagado">Nadie está cubriendo a nadie ahora.</div> : null}
			<div className="tarjeta">
				<h4>Nueva cobertura</h4>
				<div className="rejilla">
					<ElegirAsesoras candidatas={candidatas} value={ausente} onChange={setAusente} multi={false} label="Quién falta" />
					<ElegirAsesoras candidatas={candidatas.filter((c) => !ausente.includes(c.id))} value={cubre} onChange={setCubre} multi={false} label="Quién la cubre" />
					<label>Desde<input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
					<label>Hasta (último día, opcional)<input type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} /></label>
					<label>Motivo
						<select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
							{["Vacaciones", "Incapacidad", "Permiso", "Capacitación", "Otro"].map((m) => <option key={m}>{m}</option>)}
						</select>
					</label>
				</div>
				{error ? <div className="aviso error">{error}</div> : null}
				<div className="mv-botones"><button className="btn-wa pri" disabled={busy || !ausente.length || !cubre.length} onClick={guardar}>Guardar cobertura</button></div>
			</div>
			{data?.pasadas.length ? (
				<details>
					<summary className="apagado">Coberturas anteriores</summary>
					<div className="mv-lista">
						{data.pasadas.map((c) => <div key={c.id} className="mv-fila apagado">{c.cover_name} cubrió a {c.absent_name} · {dia(c.starts_on)}{c.ends_on ? " – " + dia(c.ends_on) : ""}{c.reason ? " · " + c.reason : ""}</div>)}
					</div>
				</details>
			) : null}
		</div>
	);
};

/** Pestaña "Movimientos": los últimos 30, con deshacer. */
const Movimientos: React.FC<{ campusId: number; recarga: number; onHecho: () => void }> = ({ campusId, recarga, onHecho }) => {
	const http = HttpManager.getInstance();
	const [rows, setRows] = useState<Movimiento[] | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<number | null>(null);
	const cargar = () => http.prospectsService.movimientos(campusId).then((r) => setRows(r.data.batches)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); }, [campusId, recarga]);
	const deshacer = async (b: Movimiento) => {
		setBusy(b.id); setError(null); setAviso(null);
		try {
			const r = await http.prospectsService.deshacer(campusId, b.id);
			setAviso(`Regresaron ${n(r.data.regresaron)}.` + (r.data.yaMovidas ? ` ${n(r.data.yaMovidas)} se quedaron donde están porque alguien las volvió a mover después.` : ""));
			cargar(); onHecho();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(null); }
	};
	if (!rows) return <div className="ventas-vacio">{error || "Cargando…"}</div>;
	return (
		<div className="mv-col">
			{aviso ? <div className="aviso ok">{aviso}</div> : null}
			{error ? <div className="aviso error">{error}</div> : null}
			{!rows.length ? <div className="apagado">Todavía no hay movimientos.</div> : (
				<div className="mv-lista">
					{rows.map((b) => (
						<div key={b.id} className={"mv-fila" + (b.undone_at ? " apagado" : "")}>
							<span>
								<b>{b.kind === "salida" ? "Salida de " + (b.from_name || "") : "En bloque"}</b> · {n(b.items_count)} personas
								<span className="apagado"> · {cuando(b.created_at)} · {b.created_by_name}</span><br />
								<span className="apagado">{(b.summary.porAsesora || []).filter((r) => r.n).map((r) => `${r.name} ${n(r.n)}`).join(" · ")}{b.motivo && b.motivo !== "Salida de " + (b.from_name || "") ? " · " + b.motivo : ""}</span>
							</span>
							{b.undone_at ? <span className="apagado">Deshecho · {b.undone_by_name}</span>
								: <button className="btn-wa" disabled={busy === b.id} onClick={() => deshacer(b)}>{busy === b.id ? "Deshaciendo…" : "Deshacer"}</button>}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

/** Salidas y coberturas (supervisión): cuando una asesora se va, cuando falta unos días, y el historial con deshacer. */
export const SalidasModal: React.FC<{ campusId: number; catalogs: ProspectCatalogs | null; inicial?: "seva" | "cubre" | "movimientos"; onClose: () => void; onCambio: () => void }> = ({ campusId, catalogs, inicial, onClose, onCambio }) => {
	const [pestana, setPestana] = useState<"seva" | "cubre" | "movimientos">(inicial || "seva");
	const [recarga, setRecarga] = useState<number>(0);
	const cambio = () => { setRecarga((x) => x + 1); onCambio(); };
	const tabs = useMemo(() => [{ k: "seva", t: "Se va" }, { k: "cubre", t: "Cubre (ausencia)" }, { k: "movimientos", t: "Movimientos" }] as const, []);
	return (
		<Modal show onHide={onClose} size="lg" className="ventas-nuevo-modal">
			<Modal.Header closeButton><Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>Salidas y coberturas</Modal.Title></Modal.Header>
			<Modal.Body className="mv">
				<div className="cv-modo" role="tablist">
					{tabs.map((x) => <button key={x.k} role="tab" aria-selected={pestana === x.k} className={pestana === x.k ? "on" : ""} onClick={() => setPestana(x.k)}>{x.t}</button>)}
				</div>
				{pestana === "seva" ? <SeVa campusId={campusId} onHecho={cambio} /> : pestana === "cubre" ? <Cubre campusId={campusId} catalogs={catalogs} />
					: <Movimientos campusId={campusId} recarga={recarga} onHecho={cambio} />}
			</Modal.Body>
		</Modal>
	);
};
